import type { ListBuilderConfiguration } from '../../types';
import { atRiskItemsList } from './lists/at-risk-items';
import { dueForReorderList } from './lists/due-for-reorder';
import { previouslyPurchasedList } from './lists/previously-purchased';
import { recommendedList } from './lists/recommended';

/**
 * Client Item History list configuration
 * Multi-view (4 tabs): Previously Purchased, Due for Reorder, At Risk,
 * Recommended.
 * (PD-14018 removed the old Recommended five-star grid — it surfaced
 * effectively the whole catalog, see PD-13453. PD-14016/14017 brought
 * Recommended back as a single ranked, explainable list served from the
 * nightly account-recommender precompute.)
 */
export const CLIENT_ITEM_HISTORY_CONFIG: ListBuilderConfiguration = {
	lists: [
		previouslyPurchasedList,
		dueForReorderList,
		atRiskItemsList,
		recommendedList,
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		// PD-14018: trimmed from the full Items-page mirror down to the
		// filters reps actually pivot insights on (search, category,
		// manufacturer) plus the trend window selector. itemSearch is a
		// text-type filter so it renders as a keyword input at the top of
		// the sidebar; the queryFn reads its value as the search arg.
		activeFilters: [
			'itemSearch',
			'itemCategories',
			'itemManufacturers',
			'itemHistoryTrendWindow',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/invoices/:id',
			objectLabel: 'Invoice',
			objectTable: 'invoice',
			selectedFields: [{ fieldName: 'lastInvoiceId', fieldLabel: 'ID' }],
		},
		{
			baseUrl: '/items/:id',
			objectLabel: 'Item',
			objectTable: 'item',
			selectedFields: [
				{ fieldName: 'itemId', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
				{ fieldName: 'sku', fieldLabel: 'SKU' },
				{ fieldName: 'category', fieldLabel: 'Category' },
				{ fieldName: 'subcategory', fieldLabel: 'Subcategory' },
				{ fieldName: 'manufacturer', fieldLabel: 'Manufacturer' },
				{ fieldName: 'price', fieldLabel: 'Unit Price' },
				{ fieldName: 'quantityOnHand', fieldLabel: 'Quantity On Hand' },
				{ fieldName: 'isActive', fieldLabel: 'Active' },
				{ fieldName: 'unitOfMeasure', fieldLabel: 'Unit of Measure' },
				{ fieldName: 'weight', fieldLabel: 'Weight' },
				{ fieldName: 'isHazmat', fieldLabel: 'Hazmat' },
				{ fieldName: 'isRefrigerated', fieldLabel: 'Refrigerated' },
				// Recent-window purchase metrics (served by getItemHistory). Available
				// to add as columns via Configure List, but intentionally NOT in the
				// default layout.
				{ fieldName: 'quantityLast30Days', fieldLabel: '30-Day Total' },
				{ fieldName: 'purchasesLast30Days', fieldLabel: '30-Day Orders' },
				{ fieldName: 'quantityLast60Days', fieldLabel: '60-Day Total' },
				{ fieldName: 'maxQuantityLast60Days', fieldLabel: '60-Day Max' },
				{ fieldName: 'avgQuantityLast60Days', fieldLabel: '60-Day Avg' },
				{ fieldName: 'quantityLast90Days', fieldLabel: '90-Day Total' },
				{ fieldName: 'maxQuantityLast90Days', fieldLabel: '90-Day Max' },
				{ fieldName: 'avgQuantityLast90Days', fieldLabel: '90-Day Avg' },
			],
		},
	],
};
